/**
 * Deep-link-to-a-comment URL scheme (operator-task-fe-deep-link-to-comment).
 *
 * A shareable permalink to a single comment is the record's own URL with a
 * `comment=<commentId>` query param appended — e.g.
 * `/custom_object?id=<recordId>&comment=<commentId>`. Following such a link
 * opens the record, activates its Comments tab, scrolls the target comment into
 * view and briefly highlights it.
 *
 * The SCHEME lives in exactly these functions so it has one definition: the
 * query-param key, how the link is built, how it is read back off a URL, and
 * how it is stripped after being consumed once. Switching the scheme (e.g. to a
 * `#comment-<id>` hash fragment) is a change to this file only — not to the copy
 * affordance in CommentsPanel nor the resolver in ResourcesSingle.
 *
 * The query-param form (not a hash fragment) matches the rest of the app's
 * deep-link work (advanced-search filter link, canvas `?focus=<id>`): the SPA
 * addresses every screen/record by query string and has no hash-fragment
 * router. The key is `comment` (not `tab`) because `tab` is already reserved by
 * Advanced Search's own tab state.
 */

import { FeByType, FeConfigType, RecordId } from "config/config";
import { getRelativeUrl } from "utils/service";

/** The query-param key that carries a deep-linked comment id on a record URL. */
export const COMMENT_DEEPLINK_PARAM = "comment";

/**
 * Relative in-app path that deep-links a specific comment on a record: the
 * record's own URL (`/<recordType>?<by>=<ref>`) with `&comment=<commentId>`
 * appended. Reuses {@link getRelativeUrl} so it inherits the record-addressing
 * convention. Prepend `window.location.origin` for a shareable absolute link.
 */
export const buildCommentDeepLinkPath = (
    recordType: FeConfigType,
    recordRef: RecordId,
    commentId: string,
    by: FeByType = "id"
): string =>
    `${getRelativeUrl(recordType, recordRef, by)}&${COMMENT_DEEPLINK_PARAM}=${encodeURIComponent(
        commentId
    )}`;

/**
 * The deep-linked comment id read off a URL query string (the leading "?" is
 * optional), or undefined when the param is absent/empty. Parses by splitting on
 * "&" (rather than URLSearchParams) so bare sub-page route flags in the same URL
 * — e.g. `?edit`, `?canvas=template` — are never accidentally re-encoded. Pure
 * counterpart of {@link buildCommentDeepLinkPath} so the scheme round-trips in
 * one tested unit.
 */
export const parseCommentDeepLinkParam = (
    search: string | null | undefined
): string | undefined => {
    if (!search) {
        return undefined;
    }
    const query = search.startsWith("?") ? search.slice(1) : search;
    for (const part of query.split("&")) {
        if (part.startsWith(`${COMMENT_DEEPLINK_PARAM}=`)) {
            const value = decodeURIComponent(part.slice(COMMENT_DEEPLINK_PARAM.length + 1));
            return value || undefined;
        }
    }
    return undefined;
};

/** The subset of a mouse-click used to decide how a comment permalink behaves. */
export interface CommentLinkClick {
    /** 0 = primary (left) button; a non-zero value is a non-primary click. */
    button: number;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
    /** True when an earlier handler already called preventDefault. */
    defaultPrevented: boolean;
}

/**
 * Whether a click on the per-comment permalink should scroll IN PAGE (true) rather
 * than let the browser follow the link (false). Only an unmodified primary
 * (left) click that nobody has already handled scrolls in place; a middle-click,
 * ctrl/cmd/shift/alt-click, or an already-handled event is left to the browser so
 * "open in a new tab" and the context-menu "copy link address" keep working.
 * Pure so the link's behaviour is unit-tested independently of the DOM.
 */
export const shouldScrollInPage = (click: CommentLinkClick): boolean =>
    !click.defaultPrevented &&
    click.button === 0 &&
    !click.metaKey &&
    !click.ctrlKey &&
    !click.shiftKey &&
    !click.altKey;

/**
 * The same query string with the `comment` deep-link param removed and ALL other
 * params preserved byte-for-byte (including bare route flags), so the param can
 * be consumed once and stripped from the URL (history.replace) — a refresh or
 * in-app renavigation then won't re-trigger the scroll/highlight. Returns a
 * string beginning with "?" when any params remain, else an empty string.
 */
export const stripCommentDeepLinkParam = (search: string | null | undefined): string => {
    if (!search) {
        return "";
    }
    const query = search.startsWith("?") ? search.slice(1) : search;
    const kept = query
        .split("&")
        .filter(
            (part) =>
                part !== "" &&
                part !== COMMENT_DEEPLINK_PARAM &&
                !part.startsWith(`${COMMENT_DEEPLINK_PARAM}=`)
        );
    return kept.length ? `?${kept.join("&")}` : "";
};
